import React, { Suspense } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons/svg/arrow-left";
import { ChevronDown16Regular } from "@fluentui/react-icons/svg/chevron-down";
import { ChevronRight16Regular } from "@fluentui/react-icons/svg/chevron-right";
import {
  LockClosed16Regular,
  LockClosed20Regular,
} from "@fluentui/react-icons/svg/lock-closed";
import { Person20Regular } from "@fluentui/react-icons/svg/person";
import { PersonAdd20Regular } from "@fluentui/react-icons/svg/person-add";
import { useTranslation } from "react-i18next";
import { useConfig } from "@contexts/ConfigContext";
import type { Source, Status } from "@onetasklist/shared";
import ColorPickerPopover from "@components/ColorPicker/ColorPickerPopover";
import { ConnectionSourceSettings } from "../ConnectionSourceSettings";
import { useSourceSettingsValidationMessage } from "../ConnectionSourceSettingsRegistry";
import { SettingsSectionCard } from "../source-settings/components/SettingsSectionCard";
import { SourceSingleTagPicker } from "../source-settings/components/SourceSingleTagPicker";
import { ConnectionSyncInfoPopover } from "./ConnectionSyncInfoPopover";
import type { ConnectionEditState, ConnectionEditStateUpdater } from "../Connections.ViewModel";
import {
  getSourceAuthSetup,
  sourceOwnsAuthButtons,
  sourceAuthSetupUsesDialog,
  SourceAuthSetupRenderer,
} from "../source-auth-setup";
import { ConnectionHelpPanel } from "../help";
import { ConnectionPreviewDock } from "../preview";
import { useConfigureConnectionViewModel } from "./ConfigureConnectionView.ViewModel";

interface ConfigureConnectionViewProps {
  source: Source;
  draftConnection: ConnectionEditState;
  statuses: Status[];
  isConnecting: boolean;
  /** Board the connection belongs to. Required for the task preview dock. */
  boardId?: string;
  onUpdate: ConnectionEditStateUpdater;
  onBack: () => void;
  /** Triggers auth / connection creation. Updates draft with profileName. Does NOT navigate. */
  onConnect: () => Promise<void>;
  /** Saves settings and navigates to /connections. Available only after onConnect succeeds. */
  onSave: () => void;
  /** When provided, renders a Delete button in the footer (edit mode). */
  onDelete?: () => Promise<void>;
  /** Called by source-specific auth setup components to provide pre-OAuth values (e.g. subdomain). */
  onSourceSetupChange?: (
    setup: Record<string, string>,
    isValid: boolean,
  ) => void;
  /** When true, suppresses the sticky footer — parent page owns the action buttons in its header. */
  hideFooter?: boolean;
}

export const ConfigureConnectionView: React.FC<
  ConfigureConnectionViewProps
> = ({
  source,
  draftConnection,
  statuses,
  isConnecting,
  boardId,
  onUpdate,
  onBack,
  onConnect,
  onSave,
  onDelete,
  onSourceSetupChange,
  hideFooter,
}) => {
  const { t } = useTranslation();
  const sourceSettingsValidationMessage = useSourceSettingsValidationMessage(draftConnection);
  const { config } = useConfig();
  const iconBaseUrl = config?.regionBaseUrl || "";
  const hasSourceAuthSetup = getSourceAuthSetup(source.id) !== null;
  const authSetupOwnsButtons = sourceOwnsAuthButtons(source.id);
  const authSetupUsesDialog = sourceAuthSetupUsesDialog(source.id);

  const {
    isConnected,
    needsReauthorisation,
    privacyExpanded,
    togglePrivacyExpanded,
    sourceSetupValid,
    handleSourceSetupChange,
    authSetupDialogOpen,
    closeAuthSetupDialog,
    isHelpPanelOpen,
    openHelpPanel,
    closeHelpPanel,
    defaultStatusQuery,
    setDefaultStatusQuery,
    defaultStatusOptions,
    selectedStatusLabel,
    handleConnectClick,
    handleChangeAccountClick,
    confirmAuthSetupDialog,
  } = useConfigureConnectionViewModel({
    draftConnection,
    statuses,
    usesDialogAuthFlow: authSetupUsesDialog,
    onConnect,
    onSourceSetupChange,
    defaultColumnPlaceholder: t(
      "board.connections.catalogue.configure.defaultColumnPlaceholder",
    ),
  });

  const nameLength = draftConnection.name.length;

  return (
    <div className="configure-connection-view">
      {/* Source banner */}
      <div className="configure-connection-view__banner">
        <img
          className="configure-connection-view__banner-icon"
          src={`${iconBaseUrl}/assets/img/sourceicons/${source.id.toLowerCase()}.png`}
          alt=""
        />
        <div className="configure-connection-view__banner-main">
          <div className="configure-connection-view__banner-title-row">
            <Text size={500} weight="semibold">
              {source.displayName}
            </Text>
            {source.category && (
              <Badge appearance="outline" size="small">
                {source.category}
              </Badge>
            )}
          </div>
          <Text size={200} className="configure-connection-view__banner-desc">
            {source.description}
          </Text>
        </div>
        <div className="configure-connection-view__banner-meta">
          <Link as="button" onClick={openHelpPanel}>
            {t("board.connections.catalogue.configure.learnMore")}
          </Link>
          {source.alpha && (
            <Badge appearance="filled" color="success" size="medium">
              {t("board.connections.catalogue.configure.alphaBadge")}
            </Badge>
          )}
        </div>
      </div>

      {/* Section 1: Connection Details */}
      <SettingsSectionCard
        sectionOffset={1}
        title={t("board.connections.catalogue.configure.generalSection")}
        action={
          <ConnectionSyncInfoPopover
            lastFullSync={draftConnection.lastFullSync}
            lastSync={draftConnection.lastSync}
            message={draftConnection.message}
            disableUntil={draftConnection.disableUntil}
            disabledReason={draftConnection.disabledReason}
          />
        }
      >
        <Field
          className="configure-connection-view__field-col--name"
          label={t("board.connections.catalogue.configure.nameLabel")}
          orientation="horizontal"
        >
          <Input
            value={draftConnection.name}
            maxLength={50}
            onChange={(_, data) => onUpdate({ name: data.value })}
            contentAfter={
              <Text
                size={100}
                className="configure-connection-view__char-count"
              >
                {nameLength}/50
              </Text>
            }
          />
        </Field>

        <Field
          className="configure-connection-view__field-col--colour"
          label={t("board.connections.catalogue.configure.colourLabel")}
          orientation="horizontal"
        >
          <ColorPickerPopover
            selected={draftConnection.colour}
            onSelect={(value) => onUpdate({ colour: value })}
            label={t("board.connections.catalogue.configure.colourLabel")}
            popoverTitle={t(
              "board.connections.catalogue.configure.colourLabel",
            )}
          />
        </Field>

        <Field
          className="configure-connection-view__field-col--status"
          label={t("board.connections.catalogue.configure.defaultColumnLabel")}
          orientation="horizontal"
        >
          <SourceSingleTagPicker
            selectedId={draftConnection.defaultStatusId || undefined}
            selectedLabel={selectedStatusLabel}
            query={defaultStatusQuery}
            placeholder={t(
              "board.connections.catalogue.configure.defaultColumnPlaceholder",
            )}
            options={defaultStatusOptions}
            onQueryChange={setDefaultStatusQuery}
            onSelect={(value) => onUpdate({ defaultStatusId: value ?? "" })}
            clearSelectionText={t(
              "board.connections.catalogue.configure.defaultColumnPlaceholder",
            )}
          />
        </Field>

        {/* Source-specific pre-auth setup fields (e.g. subdomain for Zendesk) — not for dialog-mode sources */}
        {hasSourceAuthSetup && !isConnected && !authSetupUsesDialog && (
          <Suspense fallback={null}>
            <SourceAuthSetupRenderer
              sourceId={source.id}
              onSetupChange={handleSourceSetupChange}
              onConnect={authSetupOwnsButtons ? onConnect : undefined}
              isConnecting={authSetupOwnsButtons ? isConnecting : undefined}
            />
          </Suspense>
        )}

        {/* Dialog-mode auth setup: shown as an overlay when Connect / Change Account / Re-auth is clicked */}
        {hasSourceAuthSetup && authSetupUsesDialog && authSetupDialogOpen && (
          <Suspense fallback={null}>
            <SourceAuthSetupRenderer
              sourceId={source.id}
              onSetupChange={handleSourceSetupChange}
              onConnect={confirmAuthSetupDialog}
              isConnecting={isConnecting}
              onDismiss={closeAuthSetupDialog}
            />
          </Suspense>
        )}

        {/* Connected account — Connect button lives here when not yet authenticated */}
        <div className="configure-connection-view__account-section">
          <Text
            size={200}
            weight="semibold"
            className="configure-connection-view__account-label"
          >
            {t("board.connections.catalogue.configure.connectedAccountLabel")}
          </Text>
          <div className="configure-connection-view__account-row">
            <div className="configure-connection-view__account-info">
              {isConnected ? (
                <>
                  <Avatar
                    name={draftConnection.profileName}
                    size={32}
                    color="brand"
                  />
                  <Text size={300} weight="semibold">
                    {draftConnection.profileName}
                  </Text>
                </>
              ) : (
                <>
                  <Avatar size={32} />
                  <Text
                    size={200}
                    className="configure-connection-view__no-account"
                  >
                    {t(
                      "board.connections.catalogue.configure.noProfileConnected",
                    )}
                  </Text>
                </>
              )}
            </div>
            <div className="configure-connection-view__account-actions">
              {isConnected ? (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Person20Regular />}
                  onClick={handleChangeAccountClick}
                  disabled={isConnecting}
                >
                  {t("board.connections.catalogue.configure.changeAccount")}
                </Button>
              ) : (
                !authSetupOwnsButtons && (
                  <Button
                    appearance="primary"
                    icon={<PersonAdd20Regular />}
                    aria-label={t(
                      "board.connections.catalogue.configure.connectButton",
                    )}
                    onClick={handleConnectClick}
                    disabled={isConnecting || !sourceSetupValid}
                  >
                    {isConnecting
                      ? t("board.connections.sources.connecting")
                      : t(
                          "board.connections.catalogue.configure.connectButton",
                        )}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </SettingsSectionCard>

      {/* Source-specific settings. Not mounted while signing in: a half-authorised
          connection (Dynamics holds only a discovery token until an environment is picked)
          would make every editor request fail. */}
      {isConnecting ? (
        <Card role="status">
          <Spinner
            size="small"
            labelPosition="after"
            label={t("board.connections.catalogue.configure.waitingForAuthorisation")}
          />
        </Card>
      ) : (
        <>
          {needsReauthorisation && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {t("board.connections.catalogue.configure.needsReauthorisation")}
              </MessageBarBody>
            </MessageBar>
          )}

          <ConnectionSourceSettings
            editingConnection={draftConnection}
            sourceDisplayName={source.displayName}
            onUpdate={onUpdate}
            sectionOffset={2}
            boardStatuses={statuses}
          />

          {isConnected && sourceSettingsValidationMessage && (
            <MessageBar intent="warning">
              <MessageBarBody>{sourceSettingsValidationMessage}</MessageBarBody>
            </MessageBar>
          )}
        </>
      )}

      {/* Data Privacy — always shown; auto-collapses after auth */}
      <Card className="configure-connection-view__section configure-connection-view__privacy-section">
        <button
          type="button"
          className="configure-connection-view__privacy-toggle"
          onClick={togglePrivacyExpanded}
        >
          <div className="configure-connection-view__privacy-toggle-left">
            <LockClosed20Regular className="configure-connection-view__privacy-lock-icon" />
            <div>
              <Text weight="semibold" block>
                {t("board.connections.catalogue.configure.privacySection")}
              </Text>
              <Text
                size={200}
                className="configure-connection-view__privacy-subtitle"
              >
                {t("board.connections.catalogue.configure.privacySubtitle", {
                  source: source.displayName,
                  defaultValue: `How OneTaskList accesses, stores and updates your ${source.displayName} data.`,
                })}
              </Text>
            </div>
          </div>
          <div className="configure-connection-view__privacy-toggle-right">
            <Badge
              appearance="outline"
              color="success"
              size="medium"
              icon={<LockClosed16Regular />}
            >
              {t("board.connections.catalogue.configure.encryptedBadge")}
            </Badge>
            {privacyExpanded ? (
              <ChevronDown16Regular className="configure-connection-view__privacy-chevron" />
            ) : (
              <ChevronRight16Regular className="configure-connection-view__privacy-chevron" />
            )}
          </div>
        </button>

        {privacyExpanded && (
          <div className="configure-connection-view__privacy-body">
            {source.securityDescription ? (
              <div
                className="configure-connection-view__privacy-content"
                dangerouslySetInnerHTML={{ __html: source.securityDescription }}
              />
            ) : (
              <Text
                size={200}
                className="configure-connection-view__privacy-empty"
              >
                {t("board.connections.catalogue.configure.privacyNoInfo", {
                  defaultValue:
                    "Data privacy information for this source is not available.",
                })}
              </Text>
            )}
          </div>
        )}
      </Card>

      <ConnectionHelpPanel
        open={isHelpPanelOpen}
        source={source}
        onDismiss={closeHelpPanel}
      />

      {/* Live preview of what the current (unsaved) configuration finds */}
      {!isConnecting && (
        <ConnectionPreviewDock
          boardId={boardId}
          draftConnection={draftConnection}
          clearsStickyFooter={!hideFooter}
        />
      )}

      {!hideFooter && (
        <div className="configure-connection-view__footer">
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={onBack}
          >
            {t("board.connections.catalogue.configure.back")}
          </Button>
          {onDelete && (
            <Button appearance="subtle" onClick={() => void onDelete()}>
              {t("board.connections.dialog.delete")}
            </Button>
          )}
          {isConnected && (
            <Button
              appearance="primary"
              disabled={!!sourceSettingsValidationMessage}
              onClick={onSave}
            >
              {t("board.connections.catalogue.configure.saveButton")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
